import { Badge, Card, CardContent, cn, Spinner } from '@estac1/ui';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { ConversationProposals, type ProposalRow } from './conversation-proposals.js';

interface MessageRow {
  role: 'user' | 'assistant';
  content: string;
  screen: string | null;
  blocked: string | null;
  detail: string | null;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  createdAt: string;
}

/** Uma conversa inteira, com a tela de cada pergunta e o custo de cada resposta. */
/** Por que a resposta saiu do histórico do modelo (#688). */
const BLOCK_LABEL: Record<string, string> = {
  off_topic: 'fora do assunto',
  injection: 'tentativa de ataque',
  refusal: 'recusada pelo modelo',
  error: 'falhou no meio',
  output: 'barrada na saída',
};

export const ConversationDetail = ({ id }: { id: string }) => {
  const detail = useQuery({
    queryKey: ['admin-assistant-conversation', id],
    queryFn: () =>
      api.request<{ messages: MessageRow[]; proposals: ProposalRow[] }>(
        `/v1/admin/assistant/conversations/${id}`,
      ),
  });
  if (detail.isLoading || !detail.data) return <Spinner />;
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        {detail.data.messages.map((msg) => (
          <div
            key={`${msg.createdAt}-${msg.role}`}
            className={cn(
              'space-y-1 rounded px-3 py-2 text-sm',
              msg.role === 'user' ? 'bg-brand/10' : 'bg-muted',
            )}
          >
            <p className="flex flex-wrap items-center gap-2 text-xs text-muted-fg">
              {msg.role === 'user' ? `Dono${msg.screen ? ` em ${msg.screen}` : ''}` : 'Assistente'}
              {msg.blocked && (
                <Badge variant="danger">
                  {BLOCK_LABEL[msg.blocked] ?? msg.blocked}
                  {msg.detail ? ` (${msg.detail})` : ''}
                </Badge>
              )}
              {msg.role === 'assistant' && (msg.blocked === null || msg.inputTokens > 0) && (
                <span>
                  {msg.inputTokens} entrada · {msg.outputTokens} saída · {msg.cacheReadTokens} do
                  cache
                </span>
              )}
            </p>
            <p className="whitespace-pre-wrap">{msg.content}</p>
          </div>
        ))}
        <ConversationProposals proposals={detail.data.proposals ?? []} />
      </CardContent>
    </Card>
  );
};
